/** Small stateless pieces: GxBadge, GxCard, GxSkeleton, GxEmptyState, GxErrorBanner, GxNotice. Style lives in theme.css. */
import { useContext, type CSSProperties, type ReactNode } from "react";
import { QueryClientContext } from "@tanstack/react-query";
import type { ErrorBody } from "@bsim/contracts";
import { errorSentence } from "../errors";
import { GxLoading } from "./GxLoading";
import "./system.css";

export type Tone = "good" | "warn" | "crit" | "info" | "neutral" | "gold";

/** Status is never colour alone: pass a glyph or word as children (▲ ▼ ● !). */
export function GxBadge({ tone, children, testId, title }: { readonly tone: Tone; readonly children: ReactNode; readonly testId?: string; readonly title?: string }) {
  return <span className={`gx-badge gx-badge--${tone} gx-num`} data-testid={testId} {...(title === undefined ? {} : { title })}>{children}</span>;
}

export function GxCard({ title, aside, children, className = "", testId }: {
  readonly title?: ReactNode; readonly aside?: ReactNode; readonly children: ReactNode; readonly className?: string; readonly testId?: string;
}) {
  return (
    <section className={`gx-card ${className}`} data-testid={testId}>
      {title === undefined ? null : <div className="gx-card-h"><h2 className="gx-h2">{title}</h2>{aside}</div>}
      {children}
    </section>
  );
}

/** Skeletons keep the final layout and never show zeros (screens §3.1). */
export function GxSkeleton({ width = "100%", height = 16 }: { readonly width?: CSSProperties["width"]; readonly height?: number }) {
  return <span className="gx-skeleton" style={{ width, height }} aria-hidden="true" />;
}

/** A loading stand-in that reserves the final height: `height` is the room of what replaces it, `lines` the grey bars drawn in it (never zeros, never a bare dash). */
export function GxSkeletonBlock({ height, lines = 3, testId }: { readonly height: number; readonly lines?: number; readonly testId?: string }) {
  return (
    <div className="gx-skel-block" style={{ minHeight: height }} aria-busy="true" data-testid={testId}>
      {Array.from({ length: lines }, (_, i) => <GxSkeleton key={i} width={i === lines - 1 ? "60%" : "100%"} height={16} />)}
    </div>
  );
}

/**
 * The wait for a page's data: the one loading card, its words shown from the first moment (F16.5 F: a bare block that lingers reads as a hang).
 * `what` is a plain phrase ("your results"); `height` reserves the room of the page that replaces it.
 */
export function GxWaiting({ what, testId, words, height = 176 }: { readonly what: string; readonly testId?: string; /** The whole sentence, when "Loading {what}…" is not enough (a wait that is expected to be long). */ readonly words?: string; readonly height?: number }) {
  return <GxLoading words={words ?? `Loading ${what}\u2026`} height={height} {...(testId === undefined ? {} : { testId })} />;
}

export function GxEmptyState({ title, children }: { readonly title: string; readonly children?: ReactNode }) {
  return <div className="gx-empty"><b className="gx-h3">{title}</b>{children === undefined ? null : <span>{children}</span>}</div>;
}

/** The limits a refused signing names (`details.limits`: one sentence each, "CRE growth is held by EV-02:C through round 3"). */
const limitsOf = (error: ErrorBody["error"] | undefined): string[] => {
  const limits: unknown = error?.details?.["limits"];
  return Array.isArray(limits) ? limits.flatMap((l: unknown) => (typeof l === "object" && l !== null && "text" in l && typeof l.text === "string" ? [l.text] : [])) : [];
};

/** The request id of a failed read (an `ApiFailure` carries the api's error body), or null for any other error. */
const failureRequestId = (e: unknown): string | null => (typeof e === "object" && e !== null && "result" in e && typeof e.result === "object" && e.result !== null && "error" in e.result && typeof e.result.error === "object" && e.result.error !== null && "requestId" in e.result.error && typeof e.result.error.requestId === "string" ? e.result.error.requestId : null);

/** Error → its content §2.7 sentence, then any limit it names; the code and request id sit under "Details". */
/** `details`: the technical code, message and request id under "Details", for the facilitator console only; a player page prints the plain sentence and nothing else. */
export function GxErrorBanner({ error, message, details = false, onRetry }: { readonly error?: ErrorBody["error"]; readonly message?: string; readonly details?: boolean; /** A read that failed: offers "Try again". Default: when this very error is a failed read in the query cache (same request id), the button refetches it. */ readonly onRetry?: () => void }) {
  const limits = limitsOf(error);
  const client = useContext(QueryClientContext);
  const failed = client === undefined || error === undefined || error.requestId === "" ? [] : client.getQueryCache().findAll({ predicate: (q) => q.state.status === "error" && failureRequestId(q.state.error) === error.requestId });
  const retry = onRetry ?? (client === undefined || failed.length === 0 ? undefined : () => { for (const q of failed) void q.fetch(); });
  return (
    <div className="gx-error" role="alert">
      <span aria-hidden="true">!</span>
      <div>
        <div>{message ?? (error === undefined ? "" : errorSentence(error))}</div>
        {retry === undefined ? null : <button type="button" className="gx-btn gx-btn--secondary" data-testid="error.retry" onClick={retry}>Try again</button>}
        {limits.length === 0 ? null : <ul data-testid="error.limits">{limits.map((l) => <li key={l}>{l}</li>)}</ul>}
        {error === undefined || !details ? null : <details><summary>Details</summary>{error.code} · {error.message}{error.requestId === "" ? "" : ` · ${error.requestId}`}</details>}
      </div>
    </div>
  );
}

export const GxNotice = ({ children }: { readonly children: ReactNode }) => <div className="gx-notice" role="status">{children}</div>;
